import { useEffect, useState } from "react";
import {
  getAnalyticsConsent,
  setAnalyticsConsent,
  CONSENT_EVENT,
} from "@/lib/analytics";

/**
 * VOIHU analytics-consent banner.
 *
 * Behaviour:
 *   • Renders only when the visitor has not yet decided (localStorage
 *     value is `null`). Users who previously accepted or rejected are
 *     not shown the banner again unless they explicitly reopen it via
 *     the "Manage analytics preference" control on /cookies.
 *   • Two equally-weighted actions ("Accept analytics" / "Reject") —
 *     no dark patterns, no styling asymmetry that nudges the user
 *     toward one answer.
 *   • Fixed at the bottom of the viewport; does not overlay critical
 *     UI on desktop, stacks cleanly on mobile.
 *   • Listens for a `voihu:open-consent` custom event so the Cookies
 *     page can reopen it from anywhere in the app.
 */
export default function ConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Only show on first visit (undecided). Wrapped in a rAF so the
    // banner never flashes during hydration.
    if (typeof window === "undefined") return;
    const decide = () => setVisible(getAnalyticsConsent() === null);
    const raf = window.requestAnimationFrame(decide);
    const onOpen = () => setVisible(true);
    window.addEventListener(CONSENT_EVENT, onOpen);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener(CONSENT_EVENT, onOpen);
    };
  }, []);

  if (!visible) return null;

  const decide = (value) => {
    setAnalyticsConsent(value);
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-labelledby="voihu-consent-title"
      aria-describedby="voihu-consent-body"
      data-testid="consent-banner"
      className="fixed inset-x-0 bottom-0 z-50 border-t oh-hairline
                 bg-[color:var(--oh-bg)]/95 backdrop-blur-sm
                 px-6 py-5 sm:px-10"
    >
      <div className="max-w-[960px] mx-auto flex flex-col md:flex-row
                      md:items-center md:justify-between gap-4">
        <div className="text-sm leading-relaxed
                        text-[color:var(--oh-ink)] max-w-[640px]">
          <div id="voihu-consent-title" className="oh-label mb-1">
            Analytics cookies
          </div>
          <p id="voihu-consent-body">
            Optional analytics cookies help VOIHU understand how people
            find and use the website. They are never used for
            advertising or profiling. See our{" "}
            <a
              href="/cookies"
              className="underline"
              data-testid="consent-cookies-link"
            >
              Cookie Policy
            </a>
            .
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3
                        md:justify-end md:shrink-0">
          <button
            type="button"
            onClick={() => decide("denied")}
            className="btn-ghost text-sm px-5 py-2"
            data-testid="consent-reject-btn"
          >
            Reject
          </button>
          <button
            type="button"
            onClick={() => decide("granted")}
            className="btn-red text-sm px-5 py-2"
            data-testid="consent-accept-btn"
          >
            Accept analytics
          </button>
        </div>
      </div>
    </div>
  );
}
